/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  decideAssistantAction,
  deleteAssistantConversation,
  fetchAssistantConversation,
  fetchAssistantConversations,
  fetchAssistantUsage,
  pollAssistantMessage,
  sendAssistantMessage,
  startCheckoutRescue,
} from '../actions/assistant';
import type {
  AbandonedCheckout,
  AssistantEvent,
  AssistantMessage,
  AssistantState,
} from '../types/assistant';

const initialState: AssistantState = {
  isOpen: false,
  conversationId: null,
  messages: [],
  conversations: [],
  usage: null,
  isSending: false,
  isLoadingConversation: false,
  queuedPrompt: null,
  error: null,
  abandonedCheckout: null,
  chatCheckoutItemId: null,
};

const isFinal = (message?: AssistantMessage) =>
  message?.status === 'done' || message?.status === 'failed';

/** A server copy of a reply replaces ours unless ours is already further on. */
const replaceMessage = (state: AssistantState, next: AssistantMessage) => {
  const index = state.messages.findIndex((m) => m.id === next.id);
  if (index === -1) return;
  const current = state.messages[index];
  if (isFinal(current) && !isFinal(next)) return;
  state.messages[index] = { ...next, seq: current.seq };
};

const assistantSlice = createSlice({
  name: 'assistant',
  initialState,
  reducers: {
    openAssistant: (
      state,
      action: PayloadAction<{ prompt?: string } | undefined>,
    ) => {
      state.isOpen = true;
      if (action.payload?.prompt) state.queuedPrompt = action.payload.prompt;
    },
    closeAssistant: (state) => {
      state.isOpen = false;
    },
    clearQueuedPrompt: (state) => {
      state.queuedPrompt = null;
    },
    startNewConversation: (state) => {
      state.conversationId = null;
      state.messages = [];
      state.error = null;
    },
    clearAssistantError: (state) => {
      state.error = null;
    },
    /** A Pro checkout was closed, or its payment sheet dismissed, unpaid. */
    checkoutAbandoned: {
      reducer: (state, action: PayloadAction<AbandonedCheckout>) => {
        state.abandonedCheckout = action.payload;
      },
      prepare: (reason?: string, path?: string) => ({
        payload: {
          at: Date.now(),
          reason,
          path: path ?? window.location.pathname + window.location.search,
        },
      }),
    },
    /** A chat card's Go Pro: the panel steps aside for the checkout. */
    openChatCheckout: (state, action: PayloadAction<string>) => {
      state.chatCheckoutItemId = action.payload;
      state.isOpen = false;
    },
    closeChatCheckout: (state) => {
      state.chatCheckoutItemId = null;
    },
    applyAssistantEvent: (state, action: PayloadAction<AssistantEvent>) => {
      const event = action.payload;
      const message = state.messages.find((m) => m.id === event.message_id);
      // A push for a conversation not on screen: its history reloads on open.
      if (!message || isFinal(message)) return;
      if (event.type === 'done' && event.message) {
        replaceMessage(state, event.message);
        return;
      }
      if ((message.seq ?? 0) >= event.seq) return;
      message.seq = event.seq;
      if (event.type === 'text' && typeof event.text === 'string') {
        message.status = 'streaming';
        message.content = event.text;
      } else if (event.type === 'step' && event.step) {
        const index = message.steps.findIndex((s) => s.id === event.step!.id);
        if (index === -1) message.steps.push(event.step);
        else message.steps[index] = event.step;
      } else if (event.type === 'block' && event.block) {
        if (!message.blocks.some((b) => b.id === event.block!.id)) {
          message.blocks.push(event.block);
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendAssistantMessage.pending, (state, action) => {
        state.isSending = true;
        state.error = null;
        // Shown at once; the server copy replaces it when the request lands.
        state.messages.push({
          id: action.meta.requestId,
          conversation_id: state.conversationId ?? '',
          role: 'user',
          status: 'done',
          content: action.meta.arg.message,
          blocks: [],
          steps: [],
        });
      })
      .addCase(sendAssistantMessage.fulfilled, (state, action) => {
        const { conversation, usage } = action.payload;
        state.isSending = false;
        state.conversationId = conversation.id;
        state.usage = usage;
        state.messages = state.messages.filter(
          (m) => m.id !== action.meta.requestId,
        );
        state.messages.push(action.payload.user_message, {
          ...action.payload.assistant_message,
          seq: 0,
        });
        state.conversations = [
          conversation,
          ...state.conversations.filter((c) => c.id !== conversation.id),
        ];
      })
      .addCase(sendAssistantMessage.rejected, (state, action) => {
        state.isSending = false;
        state.messages = state.messages.filter(
          (m) => m.id !== action.meta.requestId,
        );
        state.error = (action.payload as string) ?? 'something_went_wrong';
        if (action.payload === 'assistant_daily_limit_reached' && state.usage) {
          state.usage.remaining = 0;
        }
        if (action.payload === 'rescue_chat_limit_reached') {
          const chat = state.conversations.find(
            (c) => c.id === state.conversationId,
          );
          if (chat) chat.messages_left = 0;
        }
      })
      .addCase(startCheckoutRescue.fulfilled, (state, action) => {
        const { conversation, messages } = action.payload;
        // Opened by the user meanwhile: keep what they are looking at.
        if (!conversation || state.isOpen) return;
        state.isOpen = true;
        state.conversationId = conversation.id;
        state.messages = messages.map((m) => ({ ...m, seq: 0 }));
        state.error = null;
        state.conversations = [
          conversation,
          ...state.conversations.filter((c) => c.id !== conversation.id),
        ];
      })
      .addCase(fetchAssistantUsage.fulfilled, (state, action) => {
        state.usage = action.payload;
      })
      .addCase(fetchAssistantConversations.fulfilled, (state, action) => {
        state.conversations = action.payload;
      })
      .addCase(fetchAssistantConversation.pending, (state, action) => {
        state.isLoadingConversation = true;
        state.conversationId = action.meta.arg;
        state.messages = [];
        state.error = null;
      })
      .addCase(fetchAssistantConversation.fulfilled, (state, action) => {
        if (state.conversationId !== action.meta.arg) return;
        state.isLoadingConversation = false;
        state.messages = action.payload.messages.map((m) => ({ ...m, seq: 0 }));
      })
      .addCase(fetchAssistantConversation.rejected, (state) => {
        state.isLoadingConversation = false;
        state.conversationId = null;
      })
      .addCase(deleteAssistantConversation.fulfilled, (state, action) => {
        state.conversations = state.conversations.filter(
          (c) => c.id !== action.payload,
        );
        if (state.conversationId === action.payload) {
          state.conversationId = null;
          state.messages = [];
        }
      })
      .addCase(pollAssistantMessage.fulfilled, (state, action) => {
        replaceMessage(state, action.payload);
      })
      .addCase(decideAssistantAction.pending, (state, action) => {
        const message = state.messages.find(
          (m) => m.id === action.meta.arg.messageId,
        );
        const block = message?.blocks.find(
          (b) => b.id === action.meta.arg.actionId,
        );
        if (
          block?.type === 'action' &&
          action.meta.arg.decision === 'confirm'
        ) {
          block.status = 'running';
        }
      })
      .addCase(decideAssistantAction.fulfilled, (state, action) => {
        const index = state.messages.findIndex(
          (m) => m.id === action.payload.id,
        );
        if (index !== -1) {
          state.messages[index] = {
            ...action.payload,
            seq: state.messages[index].seq,
          };
        }
      })
      .addCase(decideAssistantAction.rejected, (state, action) => {
        const message = state.messages.find(
          (m) => m.id === action.meta.arg.messageId,
        );
        const block = message?.blocks.find(
          (b) => b.id === action.meta.arg.actionId,
        );
        if (block?.type === 'action' && block.status === 'running') {
          block.status = 'pending';
        }
      });
  },
});

export const {
  openAssistant,
  closeAssistant,
  clearQueuedPrompt,
  startNewConversation,
  clearAssistantError,
  applyAssistantEvent,
  checkoutAbandoned,
  openChatCheckout,
  closeChatCheckout,
} = assistantSlice.actions;
export default assistantSlice.reducer;
